import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Box, Divider, Alert,
  CircularProgress, InputAdornment, Chip
} from "@mui/material"
import { useState, useEffect } from "react"
import { useDispatch } from "react-redux"
import { triggerPayout } from "../../redux/admin-slices/wallet.slice"
import { v4 as uuidv4 } from "uuid"

/**
 * PayoutDialog
 *
 * Admin confirms a manual payout to a specialist.
 *
 * Safety:
 *   - Generates a UUID idempotencyKey when the dialog OPENS (not on submit).
 *     If admin closes and reopens → new key → prevents accidental double-payout
 *     from a rapid close/reopen cycle.
 *   - Amount validated client-side (> 0, <= availableBalance) before submit.
 *   - Button disabled while loading and after success.
 *
 * Props:
 *   open           — boolean
 *   onClose        — () => void
 *   specialist     — { _id, fullName, email, availableBalance, currency,
 *                      payoutPaused, flaggedForReview, stripeOnboardingComplete }
 *   onSuccess      — () => void  (called after successful payout to trigger re-fetch)
 */
export default function PayoutDialog({ open, onClose, specialist, onSuccess }) {
  const dispatch = useDispatch()
  const [amount, setAmount]     = useState("")
  const [note, setNote]         = useState("")
  const [loading, setLoading]   = useState(false)
  const [idemKey, setIdemKey]   = useState("")
  const [error, setError]       = useState("")

  // Generate a fresh idempotency key every time the dialog opens
  useEffect(() => {
    if (open) {
      setIdemKey(uuidv4())
      setAmount("")
      setNote("")
      setError("")
    }
  }, [open])

  const availableBalance = specialist?.availableBalance ?? 0
  const currency         = (specialist?.currency ?? "inr").toUpperCase()
  const displayAvail     = (availableBalance / 100).toFixed(2)

  const isBlocked = specialist?.payoutPaused || specialist?.flaggedForReview
  const isNotOnboarded = !specialist?.stripeOnboardingComplete

  const validate = () => {
    const numAmount = parseFloat(amount)
    if (!amount || isNaN(numAmount) || numAmount <= 0) {
      setError("Enter a valid amount greater than 0.")
      return false
    }
    // Convert INR display to paise for comparison
    const amountInSmallest = Math.round(numAmount * 100)
    if (amountInSmallest > availableBalance) {
      setError(`Amount exceeds available balance (${displayAvail} ${currency}).`)
      return false
    }
    if (amountInSmallest < 100) {
      setError(`Minimum payout amount is 1.00 (${currency.toLowerCase() === "usd" ? "100 cents" : "100 paise"}).`)
      return false
    }
    setError("")
    return true
  }

  const handleSubmit = async () => {
    if (!validate()) return
    setLoading(true)
    const amountInSmallest = Math.round(parseFloat(amount) * 100)
    try {
      const result = await dispatch(triggerPayout({
        specialistId:   specialist._id,
        amount:         amountInSmallest,
        currency:       specialist?.currency ?? "inr",
        note:           note.trim(),
        idempotencyKey: idemKey,
      })).unwrap()

      if (result?.payoutId) {
        onSuccess?.()
        onClose()
      }
    } catch {
      // Error toast is shown by asyncThunkWrapper
    } finally {
      setLoading(false)
    }
  }

  const canPayout = !isBlocked && !isNotOnboarded && availableBalance > 0

  return (
    <Dialog open={open} onClose={!loading ? onClose : undefined} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 700, fontSize: 20 }}>
        Trigger Payout
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        {/* Specialist info */}
        <Box sx={{ mb: 2.5, p: 2, bgcolor: "#f5f5f5", borderRadius: 2 }}>
          <Typography fontWeight={700} textTransform="capitalize" fontSize={16}>
            {specialist?.fullName}
          </Typography>
          <Typography color="text.secondary" fontSize={13}>{specialist?.email}</Typography>
          <Box sx={{ mt: 1.5, display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Chip size="small" label={`Available: ${displayAvail} ${currency}`}
              color="success" variant="outlined" sx={{ fontWeight: 600 }} />
            {specialist?.payoutPaused && (
              <Chip size="small" label="⏸ Payouts Paused" color="warning" />
            )}
            {specialist?.flaggedForReview && (
              <Chip size="small" label="🚩 Flagged for Review" color="error" />
            )}
            {isNotOnboarded && (
              <Chip size="small" label="⚠ Stripe Not Onboarded" color="default" />
            )}
          </Box>
        </Box>

        {/* Blocked states */}
        {isNotOnboarded && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Specialist has not completed Stripe Connect onboarding. Payout cannot be processed.
          </Alert>
        )}
        {specialist?.flaggedForReview && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Account is <strong>flagged for review</strong>. Unflag the account before triggering a payout.
          </Alert>
        )}
        {specialist?.payoutPaused && !specialist?.flaggedForReview && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Payouts are <strong>paused</strong> for this specialist.
            {specialist?.payoutPausedReason && ` Reason: ${specialist.payoutPausedReason}`}
            &nbsp;Resume payouts before proceeding.
          </Alert>
        )}
        {availableBalance === 0 && !isBlocked && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No available balance. There are no released funds to pay out.
          </Alert>
        )}

        {/* Amount input */}
        <TextField
          label={`Amount (${currency})`}
          type="number"
          fullWidth
          disabled={!canPayout || loading}
          value={amount}
          onChange={(e) => { setAmount(e.target.value); setError("") }}
          error={Boolean(error)}
          helperText={error || `Max: ${displayAvail} ${currency}`}
          InputProps={{
            startAdornment: <InputAdornment position="start">{currency.toLowerCase() === "usd" ? "$" : "₹"}</InputAdornment>,
          }}
          sx={{ mb: 2 }}
        />

        {/* Memo / note */}
        <TextField
          label="Note (optional)"
          fullWidth
          multiline
          rows={2}
          disabled={!canPayout || loading}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. March earnings payout"
          inputProps={{ maxLength: 200 }}
        />

        {/* Idempotency key visible for ops debugging */}
        <Typography variant="caption" color="text.disabled" sx={{ mt: 1.5, display: "block" }}>
          Idempotency key: {idemKey}
        </Typography>
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={loading} variant="outlined" color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!canPayout || loading}
          onClick={handleSubmit}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ minWidth: 140 }}
        >
          {loading ? "Processing…" : "Confirm Payout"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
